import { useEffect, useMemo } from 'react';
import { localize } from '@oneflowab/pomes';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { isEqual, isEmpty } from 'lodash';
import type { MessageTranslator } from '@oneflowab/pomes';

import agreementsReducer from '@app/reducers/entities/agreements';
import { redirectToDocumentOnDocumentCreate } from '@flow/libs/agreement/navigation-helpers';
import useFeatureFlag from '@app/hooks/use-feature-flag';
import usePrevious from '@app/hooks/use-previous';
import { useQueryStringParams } from '@app/hocs/query-string-params';
import { useFilterContext } from '@app/hocs/search/filter/context';
import { getFiltersValues } from '@app/hocs/search';
import { areAnyFiltersActive } from '@app/hocs/search/filter/utils';
import { WORKSPACE_ROOT_FOLDER_ID } from '@flow/libs/workspace/folders-constants';
import { UTM_SOURCE } from '@flow/libs/utils/utm';
import { BulkSelectionContext, useBulkSelection } from '@app/contexts/bulk-selection';

import CircularSpinner from '@app/components/icons/circular-spinner';
import { ContractCard } from '@app/components/contract-card';
import CreateContractButton from '@app/components/create-contract-button';
import { EmptySearchResultState } from '@app/components/empty-search-result-state';
import EmptyState from '@app/components/empty-state';
import { ExportDocuments } from '@app/components/export-documents';
import Pagination from '@app/components/pagination';
import { T } from '@app/components/translation';
import DocumentsWithStars from '@app/components/icons/documents-with-stars';
import Folders from '@app/components/icons/folders';
import * as BulkActions from '@app/components/bulk-actions';

import { BulkDeleteAction } from './bulk-actions-bar/actions/delete/bulk-delete-action';
import BulkMoveAction from './bulk-actions-bar/actions/move/bulk-move-action';
import style from './contract-list.module.scss';

type Pagination = ReturnType<typeof agreementsReducer.getQuerySelector>['pagination'];

type Props = {
  message: MessageTranslator;
  onPaginate: (pagination: Pagination) => void;
  query: ReturnType<typeof agreementsReducer.getQuerySelector>;
  queryName: string;
  result: Oneflow.Agreement[];
  selectedFolderId: number;
  showWorkspaceName: boolean;
  withoutWorkspaceId: boolean;
  workspace: Oneflow.Workspace;
};

export const ContractListComponent = ({
  message,
  onPaginate,
  query,
  queryName,
  result,
  selectedFolderId,
  showWorkspaceName,
  withoutWorkspaceId,
  workspace,
}: Props) => {
  const isBulkActionsBarEnabled = useFeatureFlag('temporaryBulkMoveDelete');
  const itemIds = useMemo(
    () => result.map(({ id }) => id),
    [
      result,
    ],
  );
  const bulkSelectionState = useBulkSelection({
    itemIds,
  });
  const { params } = useQueryStringParams();
  const { availableFilters, activeFilters } = useFilterContext();

  const previousWorkspace = usePrevious(workspace);
  const previousParams = usePrevious(params);

  const acl = {
    workspace: workspace.acl || {},
  };

  useEffect(() => {
    const { queryParams: currentQueryParams } = getFiltersValues(params, availableFilters.current);
    const { queryParams: previousQueryParams } = getFiltersValues(
      previousParams || {},
      availableFilters.current,
    );

    if (
      !isEqual(previousQueryParams, currentQueryParams) ||
      !isEqual(previousWorkspace?.id, workspace?.id)
    ) {
      bulkSelectionState.clearSelection();
    }
  }, [
    availableFilters,
    bulkSelectionState.clearSelection,
    params,
    previousParams,
    previousWorkspace?.id,
    workspace?.id,
  ]);

  const dispatch = useDispatch();

  const createContract = (workspaceId: Oneflow.Workspace['id']) => {
    dispatch(
      agreementsReducer.createAgreement({
        data: {
          workspaceId,
          folderId: selectedFolderId !== WORKSPACE_ROOT_FOLDER_ID ? selectedFolderId : undefined,
        },
        pipe: {
          onSuccess: redirectToDocumentOnDocumentCreate,
        },
      }),
    );
  };

  const handlePaginationChange = (pagination: Pagination) => {
    onPaginate(pagination);
    bulkSelectionState.clearSelection();
  };

  const renderEmptyState = () => {
    if (!query?.params?.q && !workspace.virtual && isEmpty(activeFilters)) {
      return (
        <EmptyState
          newStyle
          icon={<DocumentsWithStars height="250px" />}
          header={message({
            id: "Let's get started!",
            comment:
              'Empty state header. Used in document list when there are no documents in the workspace.',
          })}
          content={
            <div className={style.EmptyState}>
              <p className={style.Paragraph}>
                <T
                  _str="Create a document and it will appear on this page."
                  _comment="Empty state text. Used in document list when there are no documents in the workspace."
                />
              </p>
              <CreateContractButton
                acl={acl}
                createContract={createContract}
                isContractListView
                workspaceId={workspace.id}
                workspaceName={workspace.name}
              />
            </div>
          }
        />
      );
    }

    if (
      (query?.params?.q && query?.params?.q !== '') ||
      (!isEmpty(activeFilters) && !query?.params?.q && !workspace.virtual)
    ) {
      return <EmptySearchResultState message={message} />;
    }

    return null;
  };

  const renderEmptyFolderState = () => {
    if ((query?.params?.q && query?.params?.q !== '') || areAnyFiltersActive(activeFilters)) {
      return <EmptySearchResultState message={message} />;
    }

    return (
      <EmptyState
        newStyle
        icon={<Folders height="200px" />}
        header={message({
          id: 'No documents in this folder yet',
          comment:
            'Empty state header. Used in document list when there is no documents in the folder.',
        })}
        content={
          <div className={style.EmptyState}>
            <p className={style.Paragraph}>
              <T
                _str="Add or move a document here, or explore subfolders if they exist."
                _comment="Empty state text. Used in document list when there are no documents in the folder."
              />
            </p>
            <CreateContractButton
              acl={acl}
              createContract={createContract}
              isContractListView
              selectedFolderId={selectedFolderId}
              workspaceId={workspace.id}
              workspaceName={workspace.name}
            />
          </div>
        }
      />
    );
  };

  const renderSpinner = () => {
    if (!query.loading) {
      return null;
    }

    return <CircularSpinner className={style.LoadingSpinner} />;
  };

  const renderResult = () => {
    const containerClasses = clsx(style.ContractList, {
      [style.Loading]: query.loading,
    });

    if (!query.loading && isEmpty(result) && selectedFolderId !== -1) {
      return renderEmptyFolderState();
    }

    if (isEmpty(result) && !query.loading) {
      return renderEmptyState();
    }

    return (
      <div className={containerClasses}>
        {renderSpinner()}
        {result.map((agreement) => (
          <ContractCard
            agreement={agreement}
            key={agreement.id}
            queryName={queryName}
            showWorkspaceName={showWorkspaceName}
            withoutWorkspaceId={withoutWorkspaceId}
            trackingLocation={UTM_SOURCE.CONTRACT_LIST}
            selected={bulkSelectionState.isItemSelected(agreement.id)}
            onSelect={bulkSelectionState.toggleItemSelection}
          />
        ))}
      </div>
    );
  };

  const renderTopPagination = () => {
    if (isEmpty(result) || query.loading) {
      return null;
    }

    return (
      <div className={style.TopPagination}>
        <span>
          <T
            _str="Documents"
            _comment="Title for the document list"
          />
        </span>
        <Pagination
          currentOffset={query.pagination.offset}
          entityName={message({
            id: 'documents',
            comment: 'Document entity, to be used in a pagination info: Showing N of M documents',
          })}
          itemsPerPage={query.pagination.limit}
          onChange={handlePaginationChange}
          totalItems={query.count}
          renderWithoutPages
        />
      </div>
    );
  };

  const renderBulkActionsBar = () => {
    if (isEmpty(result) || !isBulkActionsBarEnabled) {
      return null;
    }

    return (
      <BulkActions.Bar>
        <BulkActions.Checkbox
          labelClassName={style.BulkActionsCheckboxLabel}
          label={
            bulkSelectionState.isSomeItemsSelected() ? null : (
              <T
                _str="Select all"
                _comment="Label for the select all checkbox"
              />
            )
          }
        />
        {bulkSelectionState.isSomeItemsSelected() && (
          <>
            <BulkDeleteAction queryName={queryName} />
            <BulkMoveAction queryName={queryName} />
          </>
        )}
      </BulkActions.Bar>
    );
  };

  const renderBottomPagination = () => {
    if (isEmpty(result) || query.loading) {
      return null;
    }

    return (
      <Pagination
        currentOffset={query.pagination.offset}
        entityName={
          message({
            id: 'documents',
            comment: 'Document entity, to be used in a pagination info: Showing N of M documents',
          }) as string
        }
        itemsPerPage={query.pagination.limit}
        onChange={handlePaginationChange}
        totalItems={query.count}
      />
    );
  };

  const renderExport = () => {
    if (query.loading) {
      return null;
    }

    return (
      <ExportDocuments
        folderId={selectedFolderId}
        queryName={queryName}
        workspace={workspace}
      />
    );
  };

  return (
    <BulkSelectionContext.Provider value={bulkSelectionState}>
      <div className={style.ContractListContainer}>
        {renderTopPagination()}
        {renderBulkActionsBar()}
        {renderResult()}
        <div className={style.PaginationContainer}>
          {renderBottomPagination()}
          <div className={isEmpty(result) ? style.ExportButton : undefined}>{renderExport()}</div>
        </div>
      </div>
    </BulkSelectionContext.Provider>
  );
};

export default localize<Props>(ContractListComponent);
